import posthog from "posthog-js";
import { analyticsOptOutEnabled } from "./analytics-privacy";
import { CAMPAIGN_FIELDS, captureCampaignArrival, campaignEventProperties, clearCampaignAttribution, entryCampaignParameters } from "./campaign-attribution";
import { initializeGoogleAnalytics, identifyGoogleAnalyticsUser, captureGoogleEvent, captureGoogleSignup, refreshGoogleAnalyticsPrivacy } from "./google-analytics";

const SCHEMA_VERSION = 1;
const ORGANIZATION_GROUP_TYPE = "organization";
const DEFAULT_POSTHOG_HOST = "https://l.limrun.com";
const PENDING_AUTH_COMPLETION_KEY = "analytics:pendingAuthCompletion";

export const AnalyticsEvents = {
  AUTH_STARTED: "auth_started",
  AUTH_COMPLETED: "auth_completed",
  ONBOARDING_VIEWED: "onboarding_viewed",
  ONBOARDING_INSTALL_COMMAND_COPIED: "onboarding_install_command_copied",
  INSTANCE_CREATE_REQUESTED: "instance_create_requested",
  STREAM_CONNECTED: "stream_connected",
  BILLING_CHECKOUT_STARTED: "billing_checkout_started",
  BILLING_PORTAL_STARTED: "billing_portal_started",
  BILLING_CONTACT_STARTED: "billing_contact_started",
  BILLING_CHECKOUT_SUCCESS_RETURNED: "billing_checkout_success_returned",
  BILLING_CHECKOUT_CANCELLED_RETURNED: "billing_checkout_cancelled_returned",
} as const;

export type AuthMethod = "github" | "google" | "agentid" | "email" | "sso" | "unknown";
export type StreamPlatform = "android" | "ios";
export type StreamDeviceClass = "phone" | "tablet" | "watch";
export type SubscriptionCountBucket = "0" | "1" | "2-9" | "10+";

type AnalyticsEventProperties = {
  [AnalyticsEvents.AUTH_STARTED]: {
    provider: AuthMethod;
  };
  [AnalyticsEvents.AUTH_COMPLETED]: {
    provider: AuthMethod;
    auth_flow: "console" | "cli";
    account_state: "existing" | "new";
  };
  [AnalyticsEvents.ONBOARDING_VIEWED]: {
    variant: "agent_prompt" | "agentid";
  };
  [AnalyticsEvents.ONBOARDING_INSTALL_COMMAND_COPIED]: {
    command_type: "agent_prompt" | "terminal_commands";
    copy_method: "button" | "manual_selection";
  };
  [AnalyticsEvents.INSTANCE_CREATE_REQUESTED]: {
    platform: StreamPlatform;
    device_class: StreamDeviceClass;
    has_initial_assets: boolean;
    source: "console";
  };
  [AnalyticsEvents.STREAM_CONNECTED]: {
    platform: StreamPlatform;
    source: "console";
  };
  [AnalyticsEvents.BILLING_CHECKOUT_STARTED]: {
    plan_type: "monthly" | "yearly" | "on_demand";
    subscription_count: SubscriptionCountBucket;
  };
  [AnalyticsEvents.BILLING_PORTAL_STARTED]: Record<string, never>;
  [AnalyticsEvents.BILLING_CONTACT_STARTED]: {
    reason: "volume_pricing";
  };
  [AnalyticsEvents.BILLING_CHECKOUT_SUCCESS_RETURNED]: Record<string, never>;
  [AnalyticsEvents.BILLING_CHECKOUT_CANCELLED_RETURNED]: Record<string, never>;
};

export type AnalyticsEvent =
  (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

let initialized = false;
let privacyListenersInitialized = false;
let identifiedUserTid: string | undefined;
let identifiedUserIsInternalOrTest = false;
let activeOrganizationTid: string | undefined;
let identifiedProfile = "";
let posthogPrivacyStopped = false;

// Staff traffic remains filterable when customer profiles are enriched.
export function isLimrunStaffEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const parts = email.trim().split("@");
  return (
    parts.length === 2 &&
    parts[0].length <= 64 &&
    parts[1].toLowerCase() === "limrun.com" &&
    /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/i.test(parts[0])
  );
}

// Honor Do Not Track, Global Privacy Control, and the opt-out chosen at
// limrun.com/privacy#choices (cookie shared across *.limrun.com): analytics
// never initializes and no analytics cookie is set.
export function initializeAnalytics(): void {
  try {
    if (!privacyListenersInitialized) {
      // Apply choices made in another tab, even when neither analytics client is enabled.
      window.addEventListener("focus", refreshAnalyticsPrivacy);
      document.addEventListener("visibilitychange", refreshAnalyticsPrivacy);
      privacyListenersInitialized = true;
    }
    captureCampaignArrival();
    initializeGoogleAnalytics();
  } catch {
    // Attribution must not prevent authentication or console startup.
  }
  const key = import.meta.env.VITE_POSTHOG_KEY?.trim();
  if (!key || initialized || analyticsOptOutEnabled()) return;

  const configuredHost = import.meta.env.VITE_POSTHOG_HOST?.trim();
  try {
    posthog.init(key, {
      api_host: configuredHost || DEFAULT_POSTHOG_HOST,
      loaded: (client) => {
        // The shared privacy choice is authoritative after opting back in and reloading.
        if (!analyticsOptOutEnabled() && client.has_opted_out_capturing()) {
          client.opt_in_capturing();
        }
      },
      autocapture: { dom_event_allowlist: ["click"], element_allowlist: ["a", "button"], capture_copied_text: false },
      mask_all_text: true,
      mask_all_element_attributes: true,
      capture_performance: true,
      capture_pageleave: true,
      capture_pageview: "history_change",
      disable_capture_url_hashes: true,
      before_send: (event) => {
        if (!event || analyticsOptOutEnabled() || posthogPrivacyStopped) return null;
        for (const props of [event.properties, event.properties?.$set, event.properties?.$set_once]) {
          if (!props) continue;
          for (const key of ["$current_url", "$initial_current_url", "$referrer", "$initial_referrer"]) {
            if (typeof props[key] !== "string") continue;
            // PostHog uses this marker for visits without a referring URL.
            if ((key === "$referrer" || key === "$initial_referrer") && props[key] === "$direct") continue;
            try { const url = new URL(props[key]); props[key] = url.origin + url.pathname; }
            catch { delete props[key]; }
          }
        }
        return event;
      },
      // Preserve the anonymous landing identity across limrun.com subdomains
      // so identify() connects pre-signup activity to the authenticated user.
      cross_subdomain_cookie: true,
      persistence: "localStorage+cookie",
      // Recording starts only after identifyAnalyticsUser confirms authentication.
      disable_session_recording: true,
      enable_recording_console_log: false,
      session_recording: {
        // Replays are for understanding navigation, not collecting customer data.
        maskAllInputs: true,
        maskTextSelector: "*",
        blockSelector: "img, video, canvas, .ph-no-capture",
        captureCanvas: {
          recordCanvas: false,
        },
        recordCrossOriginIframes: false,
        recordHeaders: false,
        recordBody: false,
        maskCapturedNetworkRequestFn: (request) => ({
          ...request,
          // Auth callbacks and other console URLs can contain sensitive query
          // parameters or fragments. Keep only the route path in replays.
          name: request.name.split(/[?#]/, 1)[0],
          requestHeaders: undefined,
          requestBody: null,
          responseHeaders: undefined,
          responseBody: null,
        }),
      },
    });
    initialized = true;
  } catch {
    // Analytics must never prevent the console from starting.
  }
}

function refreshAnalyticsPrivacy(): void {
  if (analyticsOptOutEnabled()) {
    clearCampaignAttribution();
    if (initialized && !posthogPrivacyStopped) {
      // Stop replay as soon as another tab changes the shared privacy choice.
      posthogPrivacyStopped = true;
      try { posthog.stopSessionRecording(); } catch { /* Continue clearing capture state. */ }
      try { posthog.reset(); } catch { /* Cleanup must not affect navigation. */ }
      // reset() clears SDK consent, so persist the opt-out after clearing identity.
      try { posthog.opt_out_capturing(); } catch { /* The before_send guard also blocks events. */ }
      identifiedUserTid = undefined;
      identifiedProfile = "";
      identifiedUserIsInternalOrTest = false;
      activeOrganizationTid = undefined;
    }
  }
  try { refreshGoogleAnalyticsPrivacy(); } catch { /* Privacy cleanup does not depend on Google. */ }
}

export function identifyAnalyticsUser(
  userTid: string,
  organizationTid?: string,
  isInternalOrTestUser = false,
  profile?: { email: string; name?: string; username?: string },
): void {
  refreshAnalyticsPrivacy();
  if (analyticsOptOutEnabled() || posthogPrivacyStopped) return;
  try { identifyGoogleAnalyticsUser(userTid, isInternalOrTestUser); } catch { /* Identity setup is best-effort. */ }
  if (!initialized) return;

  try {
    const sameUser = identifiedUserTid === userTid;
    const markAsInternal =
      isInternalOrTestUser || (sameUser && identifiedUserIsInternalOrTest);
    const profileKey = JSON.stringify(profile ?? {});
    if (!sameUser || profileKey !== identifiedProfile || (markAsInternal && !identifiedUserIsInternalOrTest)) {
      posthog.identify(userTid, {
        user_tid: userTid,
        ...profile,
        ...(markAsInternal ? { $internal_or_test_user: true } : {}),
      });
      identifiedUserTid = userTid;
      identifiedProfile = profileKey;
    }
    identifiedUserIsInternalOrTest = markAsInternal;

    if (organizationTid && activeOrganizationTid !== organizationTid) {
      posthog.group(ORGANIZATION_GROUP_TYPE, organizationTid);
      activeOrganizationTid = organizationTid;
    }

    if (!posthog.sessionRecordingStarted()) {
      // Record every authenticated session while ignoring any remote sampling
      // or trigger rules. The project-level recording switch still applies.
      posthog.startSessionRecording({
        sampling: true,
        linked_flag: true,
        url_trigger: true,
        event_trigger: true,
      });
    }
  } catch {
    // Identity setup is best-effort.
  }
}

// Preserve anonymous acquisition cookies, but clear a previous signed-in customer on session expiry.
export function resetIdentifiedAnalytics(): void {
  if (!initialized) return;
  try {
    if (identifiedUserTid || posthog.get_property("$user_id")) resetAnalytics();
  } catch {
    // Analytics persistence must not prevent the login screen from rendering.
  }
}

export function resetAnalytics(): void {
  try { identifyGoogleAnalyticsUser(null); } catch { /* Reset is best-effort. */ }
  if (!initialized) return;
  if (analyticsOptOutEnabled() || posthogPrivacyStopped) {
    refreshAnalyticsPrivacy();
    return;
  }

  try {
    posthog.stopSessionRecording();
    posthog.reset();
  } catch {
    // Reset is best-effort.
  } finally {
    identifiedUserTid = undefined;
    identifiedProfile = "";
    identifiedUserIsInternalOrTest = false;
    activeOrganizationTid = undefined;
  }
}

export function captureAnalyticsEvent<Event extends AnalyticsEvent>(
  event: Event,
  properties: AnalyticsEventProperties[Event],
): void {
  refreshAnalyticsPrivacy();
  if (analyticsOptOutEnabled()) return;
  if (event === AnalyticsEvents.AUTH_STARTED || event === AnalyticsEvents.AUTH_COMPLETED) {
    try { captureGoogleEvent(event, properties as Record<string, string>); } catch { /* Event delivery is best-effort. */ }
  }
  if (!initialized) return;

  try {
    posthog.capture(event, {
      schema_version: SCHEMA_VERSION,
      source: "console",
      ...campaignEventProperties(),
      ...properties,
      ...(event === AnalyticsEvents.AUTH_COMPLETED && identifiedUserIsInternalOrTest
        ? { $internal_or_test_user: true }
        : {}),
      ...(activeOrganizationTid
        ? {
            organization_tid: activeOrganizationTid,
            $groups: {
              [ORGANIZATION_GROUP_TYPE]: activeOrganizationTid,
            },
          }
        : {}),
    });
  } catch {
    // Event delivery is best-effort.
  }
}

export const DEMO_BOOKING_URL = "https://cal.com/team/limrun/mobile-app-dev-in-cloud";

export function prepareConsoleDemoLink(link: HTMLAnchorElement): void {
  const booking = new URL(DEMO_BOOKING_URL);
  // Retain Google's click-time linker while discarding stale metadata and unrecognized destinations.
  try {
    refreshAnalyticsPrivacy();
    const current = new URL(link.href);
    if (!analyticsOptOutEnabled() && current.origin === booking.origin && current.pathname === booking.pathname) {
      const linker = current.searchParams.get("_gl");
      if (linker) booking.searchParams.set("_gl", linker);
    }
    if (!analyticsOptOutEnabled()) {
      const entry = entryCampaignParameters();
      for (const [utm] of CAMPAIGN_FIELDS) if (entry[utm]) booking.searchParams.set(utm, entry[utm]);
      if (initialized && !posthog.has_opted_out_capturing()) {
        const distinctId = posthog.get_distinct_id();
        if (typeof distinctId === "string" && distinctId.length >= 1 && distinctId.length <= 200 && !/[^a-z0-9_-]/i.test(distinctId)) {
          booking.searchParams.set("metadata[ph_distinct_id]", distinctId);
          booking.searchParams.set("metadata[source]", "console");
          booking.searchParams.set("metadata[schema_version]", String(SCHEMA_VERSION));
          booking.searchParams.set("metadata[capture_enabled_at_click]", "true");
          const attribution = campaignEventProperties();
          for (const [utm] of CAMPAIGN_FIELDS) {
            for (const key of [utm, `first_${utm}`]) {
              const value = attribution[key];
              if (value) booking.searchParams.set(`metadata[${key}]`, value);
            }
          }
        }
      }
    }
  } catch {
    // Booking remains available if analytics or storage is unavailable.
  }
  link.href = booking.toString();
}

export function handleConsoleDemoNavigation(event: {
  currentTarget: HTMLAnchorElement;
  button: number;
  preventDefault(): void;
}): void {
  prepareConsoleDemoLink(event.currentTarget);
  if (!analyticsOptOutEnabled() || (event.button !== 0 && event.button !== 1)) return;

  // The installed linker can decorate the anchor after this handler. Bypass its default navigation after opt-out.
  event.preventDefault();
  window.open(DEMO_BOOKING_URL, "_blank", "noopener,noreferrer");
}

export function queueAuthCompletion(
  properties: AnalyticsEventProperties[typeof AnalyticsEvents.AUTH_COMPLETED],
): void {
  try {
    sessionStorage.setItem(PENDING_AUTH_COMPLETION_KEY, JSON.stringify(properties));
  } catch {
    // Completion capture is best-effort.
  }
}

export function capturePendingAuthCompletion(): void {
  try {
    const rawProperties = sessionStorage.getItem(PENDING_AUTH_COMPLETION_KEY);
    if (!rawProperties) return;

    const properties = JSON.parse(rawProperties) as Partial<
      AnalyticsEventProperties[typeof AnalyticsEvents.AUTH_COMPLETED]
    >;
    const completion: AnalyticsEventProperties[typeof AnalyticsEvents.AUTH_COMPLETED] = {
      provider: parseAuthMethod(
        typeof properties.provider === "string" ? properties.provider : null,
      ),
      auth_flow: properties.auth_flow === "cli" ? "cli" : "console",
      account_state: properties.account_state === "new" ? "new" : "existing",
    };
    captureAnalyticsEvent(AnalyticsEvents.AUTH_COMPLETED, completion);
    if (completion.account_state === "new" && completion.auth_flow === "console") {
      captureGoogleSignup(completion.provider);
    }
  } catch {
    // Ignore malformed or unavailable session storage.
  } finally {
    try {
      sessionStorage.removeItem(PENDING_AUTH_COMPLETION_KEY);
    } catch {
      // Session storage may be unavailable.
    }
  }
}

export function parseAuthMethod(value: string | null): AuthMethod {
  switch (value) {
    case "github":
    case "google":
    case "agentid":
    case "email":
    case "sso":
      return value;
    default:
      return "unknown";
  }
}
